import {Portal} from '@primer/react'
import {Dialog} from '@primer/styled-react/experimental'
import {type RefObject, useCallback, useMemo} from 'react'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from '../contexts/IssueCreateDataContext'
import {CreateIssue, type CreateIssueProps} from '../CreateIssue'
import {CreateIssueFooter} from '../CreateIssueFooter'
import {useSafeClose} from '../hooks/use-safe-close'
import {DisplayMode} from '../utils/display-mode'
import type {OnCreateProps} from '../utils/model'
import type {CreateIssueDialog$key} from './__generated__/CreateIssueDialog.graphql'
import styles from './CreateIssueDialog.module.css'
import {CreateIssueDialogHeader} from './CreateIssueDialogHeader'

type CreateIssueDialogProps = Omit<CreateIssueProps, 'currentRepository' | 'onSafeClose'> & {
  currentRepository: CreateIssueDialog$key | undefined | null
  returnFocusRef?: RefObject<HTMLElement | null>
}

export const CreateIssueDialog = ({
  onCreateSuccess,
  onCancel,
  navigate,
  currentRepository,
  isNavigatingToNew,
  returnFocusRef,
  ...props
}: CreateIssueDialogProps): React.ReactElement => {
  const {optionConfig, initialDefaultDisplayMode, setDisplayMode, displayMode, setCreateMore} =
    useIssueCreateConfigContext()
  const {usedStorageKeyPrefix} = useIssueCreateDataContext()
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment CreateIssueDialog on Repository
      @argumentDefinitions(includeTemplates: {type: "Boolean", defaultValue: false}) {
        ...CreateIssue @arguments(includeTemplates: $includeTemplates)
      }
    `,
    currentRepository,
  )

  const onCancelInternal = useCallback(() => {
    setCreateMore(false)
    setDisplayMode(initialDefaultDisplayMode)

    onCancel()
  }, [initialDefaultDisplayMode, onCancel, setCreateMore, setDisplayMode])

  const {onSafeClose} = useSafeClose({
    storageKeyPrefix: usedStorageKeyPrefix,
    issueFormRef: props.issueFormRef,
    onCancel: onCancelInternal,
  })

  const resetDisplayModeOnCreateSuccess = useCallback(
    ({issue, createMore}: OnCreateProps) => {
      onCreateSuccess({issue, createMore})
      if (!createMore) {
        setDisplayMode(initialDefaultDisplayMode)
      }
    },
    [initialDefaultDisplayMode, onCreateSuccess, setDisplayMode],
  )

  const issueDialogProps = {...props, onCancel: onSafeClose}

  const showCreateDialog = useMemo(() => {
    return !isNavigatingToNew || !optionConfig.canBypassTemplateSelection
  }, [isNavigatingToNew, optionConfig.canBypassTemplateSelection])

  return (
    <Portal>
      <Dialog
        returnFocusRef={returnFocusRef}
        renderHeader={headerProps => <CreateIssueDialogHeader navigate={navigate} {...headerProps} />}
        renderFooter={() => {
          if (displayMode === DisplayMode.IssueCreation || displayMode === DisplayMode.IssueDuplication) {
            return (
              <Dialog.Footer>
                <CreateIssueFooter onClose={() => onSafeClose()} {...props} />
              </Dialog.Footer>
            )
          }
          return null
        }}
        renderBody={() => (
          <div
            className={`${
              displayMode === DisplayMode.IssueCreation || displayMode === DisplayMode.IssueDuplication ? 'p-3' : 'p-0'
            }`}
          >
            <CreateIssue
              onCreateSuccess={resetDisplayModeOnCreateSuccess}
              navigate={navigate}
              currentRepository={data}
              {...issueDialogProps}
            />
          </div>
        )}
        onClose={onSafeClose}
        width="xlarge"
        height="auto"
        sx={{
          visibility: showCreateDialog ? 'visible' : 'hidden',
        }}
        className={styles.CreateIssueDialogContainer}
      />
    </Portal>
  )
}

try{ CreateIssueDialog.displayName ||= 'CreateIssueDialog' } catch {}